import { useEffect, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { api, gbp, vLabel } from "@/lib/api";
import { dailyHistory, HISTORY_RANGES } from '@/lib/priceHistory';

const COLORS = ["#E3350D", "#2563EB", "#F59E0B", "#10B981", "#8B5CF6"];
const short = (d) => d?.slice(5);

function HistoryRange({ days, onChange, label }) {
  return <label className="text-sm font-bold">Show <select aria-label={label} value={days} onChange={event => onChange(Number(event.target.value))} className="ml-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
    {HISTORY_RANGES.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
  </select></label>;
}

export function CollectionValueChart() {
  const [pts, setPts] = useState(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setPts(null); setError(false);
    api.get('/collection/history', { params: { days }, signal: controller.signal }).then(({ data }) => { if (!controller.signal.aborted) setPts(data); })
      .catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [days]);
  if (!pts) return <p role="status" className="text-sm text-slate-500">{error ? 'Collection value history is unavailable. Please try again later.' : 'Loading collection value history…'}</p>;
  const first = pts[0]?.value_gbp || 0, last = pts[pts.length - 1]?.value_gbp || 0;
  const diff = last - first;
  return (
    <div className="rounded-3xl border border-slate-200/60 bg-white p-5 sm:p-6" data-testid="collection-value-chart">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Collection value over time</p>
          <p className="font-display text-3xl font-black">{gbp(last)}</p>
        </div>
        {pts.length > 1 && <span className={`rounded-full px-3 py-1 text-sm font-bold ${diff >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`} data-testid="collection-value-change">{diff >= 0 ? "+" : ""}{gbp(diff)} since {pts[0].date}</span>}
        <HistoryRange days={days} onChange={setDays} label="Collection history range" />
      </div>
      <p className="mb-3 text-sm text-slate-500">Daily collection values saved for at least a year. Unrecorded dates remain blank.</p>
      {pts.length < 2 ? (
        <p className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-600">{pts.length ? `First saved value in this range: ${pts[0].date}.` : 'No saved values in this range.'} A new point is saved every day; earlier values cannot be reconstructed.</p>
      ) : (
        <div className="h-56">
          <ResponsiveContainer>
            <AreaChart data={dailyHistory(pts, days)} margin={{ left: 0, right: 8, top: 8 }}>
              <defs><linearGradient id="valFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FFCC00" stopOpacity={0.6} /><stop offset="100%" stopColor="#FFCC00" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="date" tickFormatter={short} fontSize={12} />
              <YAxis tickFormatter={(v) => `£${v}`} fontSize={12} width={60} />
              <Tooltip formatter={(v) => gbp(v)} />
              <Area type="monotone" dataKey="value_gbp" name="Value (GBP)" stroke="#E3350D" strokeWidth={3} fill="url(#valFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function CardPriceChart({ cardId }) {
  const [h, setH] = useState(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setH(null); setError(false);
    api.get(`/cards/${cardId}/history`, { params: { days }, signal: controller.signal }).then(({ data }) => { if (!controller.signal.aborted) setH(data); })
      .catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [cardId, days]);
  const quoted = h?.points.filter(point => h.variants.some(variant => point[`${variant}_gbp`] != null)) || [];
  if (!h) return <p role="status" className="text-sm text-slate-500">{error ? 'Price history is unavailable. Please try again later.' : 'Loading price history…'}</p>;
  const trend = h.cardmarket_trend;
  return (
    <div className="rounded-3xl border border-slate-200/60 bg-white p-5" data-testid="card-price-chart">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3"><h2 className="font-display text-xl font-bold">Price history</h2><HistoryRange days={days} onChange={setDays} label="Price history range" /></div>
      <p className="mb-3 text-sm text-slate-500">Daily market prices in GBP, saved locally for at least a year. Owned, wishlisted and previously viewed cards are tracked daily. Viewing this chart never contacts the price provider.</p>
      <p className="mb-4 text-xs text-slate-500">{quoted.length ? `${quoted.length} saved price ${quoted.length === 1 ? 'day' : 'days'} in this range, starting ${quoted[0].date}.` : 'No saved prices in this range.'} Unrecorded dates and unavailable quotes stay blank; earlier prices cannot be reconstructed.</p>
      {!!quoted.length && <div className="h-56">
        <ResponsiveContainer>
            <LineChart data={dailyHistory(h.points, days, h.range?.end)} margin={{ left: 0, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="date" tickFormatter={short} fontSize={12} />
              <YAxis tickFormatter={(v) => `£${v}`} fontSize={12} width={55} />
              <Tooltip formatter={(v) => gbp(v)} /><Legend />
               {h.variants.map((v, i) => <Line key={v} type="linear" dataKey={`${v}_gbp`} name={vLabel(v)} stroke={COLORS[i % 5]} strokeWidth={3} dot={{ r: 2 }} connectNulls={false} />)}
            </LineChart>
        </ResponsiveContainer>
      </div>}
      {!!trend?.length && <details className="mt-4 text-sm"><summary className="cursor-pointer font-bold">Saved Cardmarket averages (not historical daily quotes)</summary><p className="my-2 text-xs text-slate-500">Saved {h.summary_date}. Rolling averages and trend are provider summaries, not prices observed on those earlier dates. Holo is an aggregate, not an exact reverse/stamped quote.</p><ul className="space-y-1">{trend.map(point => <li key={point.date}>{point.label || point.date}: Normal {gbp(point.normal)} · Holo aggregate {gbp(point.holo)}</li>)}</ul></details>}
    </div>
  );
}
